// La chaîne espèces — ce que l'étape 05b produit, ce que la 10 en consomme, ce
// que la 11 en dicte. Un seul module parce que ces règles se répondent : les
// séparer ferait naître des raccordements inventés.
//
// Deux règles gouvernent l'ensemble :
//
//   • UNE ENVELOPPE DE PRÉLÈVEMENT VAUT 500 €, sans exception — y compris en
//     composition libre. C'est une règle dure : une composition qui ne tombe
//     pas juste est refusée, avec le manque ou l'excédent chiffré.
//   • SEULES LES ENVELOPPES TRAVERSENT vers le coffre. Les pièces et les
//     billets en vrac du vidage sont physiquement mêlés au coffre, donc déjà
//     saisis par le comptage de l'étape 10 : les compter une seconde fois les
//     doublerait. Les enveloppes, elles, sont scellées — jamais recomptées,
//     mais bien présentes.

import { BILLET_DENOMS, BILLET_KEY, computeTotals } from 'sotcp-core/inventaireCoffreCalc'
import { VALEUR_ENVELOPPE } from './derive'

/** Les coupures qu'un automate rend, et qu'on met en enveloppe. */
export const COUPURES = BILLET_DENOMS as number[]

export interface LigneCoupure { denomination: number; quantite: number }

export interface EnveloppeSco {
  id: string
  /** Une des quatre compositions standard, ou une composition libre. */
  libre: boolean
  detail: LigneCoupure[]
}

export interface PrelevSco {
  /** Un total unique en euros — pas de détail par dénomination (§3.1). */
  pieces: number
  /** Les billets restés en vrac, par coupure. */
  billets: Record<string, number>
  enveloppes: EnveloppeSco[]
  /** Le montant annoncé par ORIKA, saisi à la main. */
  orika: number | null
}

export const PRELEV_SCO_VIDE: PrelevSco = {
  pieces: 0, billets: {}, enveloppes: [], orika: null,
}

/**
 * Les quatre compositions standard. Elles ne sont pas réécrites ici : ce sont
 * celles du coffre (`J1_PRESETS`, sotcp-core), les mêmes enveloppes de 500 €.
 * Une seconde liste finirait par contredire la première.
 */
export interface Composition { label: string; detail: LigneCoupure[] }

export function compositionsStandard(
  presets: Array<{ label: string; detail: Array<{ denomination: number; quantite: number }> }>,
): Composition[] {
  return presets.map((p) => ({
    label: p.label,
    detail: p.detail.map((d) => ({ denomination: d.denomination, quantite: d.quantite })),
  }))
}

// ── La règle des 500 € ─────────────────────────────────────────────────────

const cents = (n: number): number => +(Number(n) || 0).toFixed(2)

export const totalDetail = (detail: LigneCoupure[]): number =>
  cents(detail.reduce((s, l) => s + (Number(l.denomination) || 0) * (Number(l.quantite) || 0), 0))

export interface Verdict {
  ok: boolean
  total: number
  /** Positif = excédent, négatif = manque. Zéro quand la composition tombe juste. */
  ecart: number
  /** Ce qu'il faut dire à l'écran, jamais un simple « invalide ». */
  message: string
}

/**
 * Une composition libre est refusée tant qu'elle ne vaut pas exactement 500 €.
 * Le verdict porte le CHIFFRE du manque ou de l'excédent : « il manque 50 € »
 * se corrige, « composition invalide » se subit.
 */
export function verifierEnveloppe(detail: LigneCoupure[]): Verdict {
  const total = totalDetail(detail)
  const ecart = cents(total - VALEUR_ENVELOPPE)
  if (ecart === 0) return { ok: true, total, ecart, message: 'la composition tombe juste' }
  return {
    ok: false, total, ecart,
    message: ecart < 0
      ? `il manque ${fmt(-ecart)} — une enveloppe vaut ${fmt(VALEUR_ENVELOPPE)}`
      : `excédent de ${fmt(ecart)} — une enveloppe vaut ${fmt(VALEUR_ENVELOPPE)}`,
  }
}

const fmt = (n: number): string =>
  n.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €'

// ── Les totaux du vidage ───────────────────────────────────────────────────

export const totalBillets = (billets: Record<string, number>): number =>
  cents(COUPURES.reduce((s, d) => s + (Number(billets[BILLET_KEY(d)]) || 0) * d, 0))

export const totalEnveloppes = (envs: EnveloppeSco[]): number =>
  cents(envs.length * VALEUR_ENVELOPPE)

export interface TotauxSco {
  pieces: number
  billets: number
  enveloppes: number
  total: number
  /** ORIKA − notre total. Nul quand ORIKA n'a pas été saisi. */
  ecartOrika: number
  orikaSaisi: boolean
}

/**
 * Aucune vérification automatique n'est possible le jour même : ces montants
 * concernent la journée en cours, dont les fichiers ORIKA n'existent pas
 * encore. Le rapprochement est donc une SAISIE, et l'écart qu'on en tire est
 * une constatation — pas un contrôle (§3.2).
 */
export function totauxSco(p: PrelevSco | null): TotauxSco {
  const s = p ?? PRELEV_SCO_VIDE
  const pieces = cents(s.pieces)
  const billets = totalBillets(s.billets ?? {})
  const enveloppes = totalEnveloppes(s.enveloppes ?? [])
  const total = cents(pieces + billets + enveloppes)
  const orikaSaisi = typeof s.orika === 'number' && Number.isFinite(s.orika)
  return {
    pieces, billets, enveloppes, total,
    ecartOrika: orikaSaisi ? cents((s.orika as number) - total) : 0,
    orikaSaisi,
  }
}

// ── Ce qui traverse vers le coffre ─────────────────────────────────────────

/**
 * Les enveloppes du vidage, converties en nombre de billets par coupure.
 *
 * Le handoff pose la formule `enveloppes × 500 / valeur du billet`, qui ne vaut
 * que pour une enveloppe d'une seule coupure. On passe par le DÉTAIL, qui la
 * redonne exactement pour les quatre compositions standard et qui traite au
 * passage la composition libre — laquelle se décompose coupure par coupure.
 */
export function billetsDesEnveloppes(envs: EnveloppeSco[]): Record<string, number> {
  const out: Record<string, number> = {}
  for (const e of envs) {
    for (const l of e.detail ?? []) {
      const d = Number(l.denomination) || 0
      const q = Math.max(0, Math.round(Number(l.quantite) || 0))
      if (!d || !q) continue
      out[BILLET_KEY(d)] = (out[BILLET_KEY(d)] ?? 0) + q
    }
  }
  return out
}

export interface LigneConsolidee {
  denomination: number
  /** Ce que Kamel a compté en vrac dans le coffre. */
  comptes: number
  /** Ce qui vient des enveloppes scellées de l'étape 05b. */
  enveloppes: number
  total: number
  valeur: number
}

/**
 * Le tableau des billets tel que l'étape 10 le montre et tel que la 11 le
 * dicte : les deux termes en clair, puis leur somme. Un total qu'on ne peut pas
 * vérifier d'un coup d'œil n'est pas vérifié.
 */
export function consoliderBillets(
  comptes: Record<string, number>, envs: EnveloppeSco[],
): { lignes: LigneConsolidee[]; total: number } {
  const desEnv = billetsDesEnveloppes(envs)
  const lignes = COUPURES.map((d) => {
    const c = Math.max(0, Math.round(Number(comptes[BILLET_KEY(d)]) || 0))
    const e = desEnv[BILLET_KEY(d)] ?? 0
    return { denomination: d, comptes: c, enveloppes: e, total: c + e, valeur: cents((c + e) * d) }
  })
  return { lignes, total: cents(lignes.reduce((s, l) => s + l.valeur, 0)) }
}

/**
 * Ce que les enveloppes SCO ajoutent au COMPTAGE du coffre.
 *
 * `computeTotals` (sotcp-core) et son miroir SQL `fc_totaux_coffre` ne les
 * connaissent pas : elles sont écrites hors de `inventaireCoffre`, dans
 * `rapport.prelevSco`. On ajoute donc le terme À CÔTÉ, des deux côtés, plutôt
 * que de mêler des billets non comptés à `inv.billets` — où plus rien ne
 * distinguerait ensuite ce qui a été compté de ce qui a été déduit.
 */
export const enveloppesAuCoffre = (p: PrelevSco | null): number =>
  totalEnveloppes(p?.enveloppes ?? [])

/** Lecture défensive du blob : tout ce qui vient de la base peut être n'importe quoi. */
export function lirePrelevSco(rapport: unknown): PrelevSco | null {
  const r = rapport as { prelevSco?: unknown } | null | undefined
  const p = r?.prelevSco
  if (!p || typeof p !== 'object' || Array.isArray(p)) return null
  const o = p as Record<string, unknown>
  const envs = Array.isArray(o.enveloppes) ? o.enveloppes : []
  return {
    pieces: Number(o.pieces) || 0,
    billets: (o.billets && typeof o.billets === 'object' && !Array.isArray(o.billets)
      ? o.billets : {}) as Record<string, number>,
    enveloppes: envs.map((e, i) => {
      const x = (e ?? {}) as Record<string, unknown>
      const detail = Array.isArray(x.detail) ? x.detail : []
      return {
        id: typeof x.id === 'string' ? x.id : `env-${i}`,
        libre: x.libre === true,
        detail: detail.map((l) => {
          const y = (l ?? {}) as Record<string, unknown>
          return { denomination: Number(y.denomination) || 0, quantite: Number(y.quantite) || 0 }
        }),
      }
    }),
    orika: typeof o.orika === 'number' && Number.isFinite(o.orika) ? o.orika : null,
  }
}

// ── Les totaux du coffre, enveloppes comprises ─────────────────────────────

export interface TotauxCoffre {
  total_compte: number
  solde_attendu: number
  ecart: number
  prelevementsJTotal: number
  /** Ce que les enveloppes scellées de l'étape 05b ajoutent au comptage. */
  enveloppesSco: number
}

/**
 * `computeTotals` (sotcp-core) reste la source des cinq termes historiques —
 * les chiffres affichés doivent rester ceux de SotCP au centime. Le sixième,
 * les enveloppes du vidage SCO, s'ajoute par-dessus : sotcp-core est partagé
 * avec la V2, qui ne connaît pas `prelevSco`, et le modifier changerait ses
 * totaux à elle.
 *
 * Le miroir SQL `fc_totaux_coffre` fait exactement la même addition — c'est lui
 * qui arbitre à la validation, et la parité des deux est vérifiée par test.
 */
export function totauxCoffre(args: {
  inv: Record<string, unknown>
  apportsTotal?: number
  prelevSco?: PrelevSco | null
}): TotauxCoffre {
  const base = computeTotals({ inv: args.inv, apportsTotal: args.apportsTotal ?? 0 })
  const env = enveloppesAuCoffre(args.prelevSco ?? null)
  return {
    total_compte: cents(base.total_compte + env),
    solde_attendu: base.solde_attendu,
    ecart: cents(base.ecart + env),
    prelevementsJTotal: base.prelevementsJTotal,
    enveloppesSco: env,
  }
}

// ── La lampe de la branche 05b ─────────────────────────────────────────────

/**
 * La branche est prête quand le comptage tient debout : aucune enveloppe hors
 * des 500 €, et le rapprochement ORIKA saisi.
 *
 * L'écart avec ORIKA, lui, ne bloque RIEN. C'est une constatation, pas un
 * contrôle : les fichiers du jour n'existent pas encore, et un écart trouvé le
 * matin est précisément l'information qu'on veut faire remonter au bilan — pas
 * une raison d'empêcher d'avancer.
 */
export function pretSco(r: unknown): { ok: boolean; texte: string } {
  const p = lirePrelevSco(r)
  if (!p) return { ok: false, texte: 'vidage non compté' }
  const t = totauxSco(p)
  const fausses = p.enveloppes.filter((e) => !verifierEnveloppe(e.detail).ok).length
  if (fausses > 0) {
    return {
      ok: false,
      texte: `${fausses} enveloppe${fausses > 1 ? 's' : ''} ne vau${fausses > 1 ? 'ent' : 't'} pas 500 €`,
    }
  }
  if (t.total === 0) return { ok: false, texte: 'vidage non compté' }
  if (!t.orikaSaisi) return { ok: false, texte: 'montant ORIKA non saisi' }
  const suffixe = t.ecartOrika === 0 ? 'conforme à ORIKA'
    : `écart ORIKA ${t.ecartOrika > 0 ? '+' : ''}${t.ecartOrika.toFixed(2).replace('.', ',')} €`
  return { ok: true, texte: `${t.total.toFixed(2).replace('.', ',')} € comptés · ${suffixe}` }
}

// ── Le semis des caisses fixes ─────────────────────────────────────────────

/**
 * Vrai quand la journée ne porte AUCUNE caisse fixe et que l'écran va donc les
 * semer depuis les réglages — 102 € et 540 € confirmés d'office.
 *
 * POURQUOI CETTE QUESTION EXISTE. Le semis n'était qu'en mémoire. L'écran
 * affichait « Caisses fixes · 642,00 € » et les comptait dans le total ;
 * `fc_totaux_coffre` ne somme que ce qui est EN BASE, et la base n'avait rien.
 * L'écran annonçait donc un écart nul pendant que le serveur en voyait un de
 * −642 €, et la validation réclamait un motif pour un écart invisible.
 *
 * Les journées d'avant portaient bien leurs 642 € : elles venaient de SotCP V2,
 * qui écrivait le blob entier. FlashCP n'écrit qu'une section touchée — et une
 * caisse fixe qu'on laisse à sa valeur habituelle, personne ne la touche jamais.
 * C'est exactement le cas normal qui ne s'écrivait pas.
 *
 * Un montant affiché, compté, et jamais écrit est un mensonge d'écran. Le semis
 * se persiste, ou il ne se compte pas.
 */
export function semisFixesAEcrire(brutCaisseFixes: unknown): boolean {
  return !Array.isArray(brutCaisseFixes) || brutCaisseFixes.length === 0
}
